import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Row,
} from "reactstrap";

import {
  DefaultButton,
  H3,
  H4,
  H5,
  P,
  SecondaryButton,
} from "../../AbstractElements";
import { courseTypeMap } from "../../Api/constants";
import DateRangeDisplay from "../../CommonElements/DateRangeDisplay";
import Divider from "../../CommonElements/Divider";
import FixedFooter from "../../CommonElements/FixedFooter";
import LabelTooltip from "../../CommonElements/LabelTooltip";
import CommonModal from "../../CommonElements/Modal";
import StarLikertScale from "../../CommonElements/StarLikertScale";
import StatusDisplay from "../../CommonElements/StatusDisplay";
import CourseContentTable from "../../container/FeedbackCourseContent";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import {
  getTrainingImpression,
  setTrainingImpressionToInitial,
  updateTrainingImpression,
} from "../../ReduxToolkit/Reducers/CourseSlice";
import {
  AddComment,
  CommentOnTrainingImpressionFromRm,
  Confirm,
  ConfirmSubmitTrainingImpression,
  Description,
  Enter,
  ErrorParsingUrlData,
  Feedback,
  Frequency,
  Instructor,
  LearningOutcomes,
  Location,
  OneOfTheRatingIsLow,
  Participant,
  PleaseAddComment,
  PleaseRateYourLevelAgreement,
  PostTest,
  PreTest,
  ProgramType,
  SaveAsDraft,
  Score,
  Submit,
} from "../../utils/Constant";
import {
  formatString,
  showToast,
  trainingImpressionFrequencyMap,
} from "../../utils/helper/helper";
import { decrypt } from "../../utils/helper/localStorageutils";
import useIsMobile from "../../utils/helper/responsive";

interface QuestionData {
  id: number;
  category: string;
  learning_outcome_id: number;
  question_text: string;
  answer_type: string;
  response?: string | number | null;
}

const TrainingImpressions = () => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comments, setComments] = useState("");
  const [searchParams] = useSearchParams();
  const [isReportMode, setIsReportMode] = useState(true);
  const [trainingFeedbackId, setTrainingFeedbackId] = useState("");
  const { trainingImpression } = useAppSelector(state => state.course);
  const [questionsData, setQuestionsData] = useState<QuestionData[]>([]);

  useEffect(() => {
    const paramValues = searchParams.get("data") || "{}";
    try {
      const data = decodeURIComponent(paramValues);
      const decryptedData = decrypt(data);
      setIsReportMode(decryptedData?.isReportMode ?? true);
      setTrainingFeedbackId(decryptedData?.feedbackId || "");
    } catch (error) {
      console.error(ErrorParsingUrlData, error);
    }
  }, [searchParams]);

  useEffect(() => {
    if (trainingFeedbackId) {
      dispatch(getTrainingImpression({ trainingFeedbackId }));
    }
  }, [trainingFeedbackId]);

  useEffect(() => {
    if (trainingImpression?.training_feedback_response?.length > 0) {
      const convertedQuestions: QuestionData[] =
        trainingImpression.training_feedback_response.map(item => ({
          id: item.training_feedback_response_id,
          learning_outcome_id: item.learning_outcome_id,
          category: LearningOutcomes,
          question_text: item.learning_outcome,
          answer_type: "star",
          response: item.rating,
        }));
      setQuestionsData(convertedQuestions);
    }
  }, [trainingImpression]);

  const toggleModal = () => {
    if (isModalOpen) {
      setComments("");
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
    }
  };

  const handlePrimaryBtnClick = async () => {
    const hasLowRatingOrUnanswered = questionsData.some(item => {
      const responseStr = (item.response ?? "").toString().trim();
      if (!responseStr || isNaN(Number(responseStr))) {
        return true;
      }
      const rating = parseInt(responseStr, 10);
      return rating < 4;
    });

    if (hasLowRatingOrUnanswered && comments.trim() === "") {
      showToast(PleaseAddComment, "error");
      return;
    }
    const payload = {
      status: "submitted",
      comment: hasLowRatingOrUnanswered ? comments : undefined,
      training_feedback_id: parseInt(trainingFeedbackId || "0", 10),
      training_feedback_response: questionsData.map(item => ({
        training_feedback_response_id: item.id,
        learning_outcome_id: item.learning_outcome_id,
        rating: item.response
          ? parseInt(item.response?.toString() || "0", 10)
          : null,
      })),
    };
    dispatch(updateTrainingImpression({ payload })).then(() => {
      dispatch(setTrainingImpressionToInitial());
      toggleModal();
      navigate(`${process.env.PUBLIC_URL}/action-menu`);
    });
  };

  const renderModalBody = () => {
    const hasLowRatingOrUnanswered = questionsData.some(item => {
      const responseStr = (item.response ?? "").toString().trim();
      if (!responseStr || isNaN(Number(responseStr))) {
        return true;
      }
      const rating = parseInt(responseStr, 10);
      return rating < 4;
    });
    return (
      <>
        <H3>{ConfirmSubmitTrainingImpression}</H3>
        {hasLowRatingOrUnanswered && (
          <>
            <H5 className="mt-3">{OneOfTheRatingIsLow}</H5>
            <FormGroup>
              <LabelTooltip
                label={AddComment}
                tooltipText={`${Enter} ${Description}`}
                important
              />
              <Input
                onChange={event => {
                  if (
                    (event.target.value && event.target.value.length <= 255) ||
                    event.target.value === ""
                  ) {
                    setComments(event.target.value);
                  }
                }}
                rows={4}
                name="objective"
                type="textarea"
                placeholder={`${Enter} ${Description}`}
                maxLength={255}
              />
            </FormGroup>
          </>
        )}
      </>
    );
  };

  const getModalTitle = () => {
    return `${Submit} ${Feedback}`;
  };
  const handleFinish = async type => {
    if (type === "submitted") {
      toggleModal();
    } else if (type === "draft") {
      const payload = {
        status: "draft",
        comment: "",
        training_feedback_id: parseInt(trainingFeedbackId || "0", 10),
        training_feedback_response: questionsData.map(item => ({
          training_feedback_response_id: item.id,
          learning_outcome_id: item.learning_outcome_id,
          rating: item.response
            ? parseInt(item.response?.toString() || "0", 10)
            : null,
        })),
      };
      dispatch(updateTrainingImpression({ payload })).then(() => {
        dispatch(setTrainingImpressionToInitial());
        navigate(`${process.env.PUBLIC_URL}/action-menu`);
      });
    }
  };

  const handleRating = (id: number, rate: number) => {
    setQuestionsData(prevData =>
      prevData.map(question =>
        question.id === id
          ? { ...question, response: rate.toString() }
          : question
      )
    );
  };

  const handleInputChange = (id: number, value: string) => {
    setQuestionsData(prevData =>
      prevData.map(question =>
        question.id === id ? { ...question, response: value } : question
      )
    );
  };

  const groupQuestionsByCategory = () => {
    return (
      questionsData?.reduce(
        (grouped, question) => {
          if (!grouped[question.category]) {
            grouped[question.category] = [];
          }
          grouped[question.category].push(question);
          return grouped;
        },
        {} as Record<string, QuestionData[]>
      ) || {}
    );
  };

  return (
    <div className="page-body">
      <CommonModal
        sizeTitle={getModalTitle()}
        modalBodyClassName=""
        isOpen={isModalOpen}
        toggle={toggleModal}
        backdrop="static"
        size="lg"
        showFooter
        onPrimaryBtnClick={handlePrimaryBtnClick}
        primaryBtnText={Confirm}
      >
        {renderModalBody()}
      </CommonModal>
      <div className="container-fluid">
        <Card>
          <CardBody className="m-2">
            <H3 className="fw-bold">{trainingImpression?.course_name}</H3>
            <Row className="mt-3">
              <Col lg="4" className="mt-1">
                <DateRangeDisplay
                  startDate={trainingImpression?.start_date}
                  endDate={trainingImpression?.end_date}
                />
              </Col>
              <Col className="d-flex justify-content-between mt-3"></Col>
            </Row>

            <div className={!isMobile ? "mt-4" : ""}>
              <Row className={isMobile ? "gx-3" : "gx-5"}>
                <Col md="4" className={`${isMobile ? "mt-2" : "my-2"}`}>
                  <StatusDisplay
                    label={Location}
                    number={trainingImpression?.location_name}
                  />
                </Col>
                <Col md="4" className={`${isMobile ? "mt-2" : "my-2"}`}>
                  <StatusDisplay
                    label={ProgramType}
                    number={courseTypeMap[trainingImpression?.program_type]}
                  />
                </Col>
                <Col md="4" className={`${isMobile ? "mt-2" : "my-2"}`}>
                  <StatusDisplay
                    label={Instructor}
                    number={trainingImpression?.primary_trainer_name}
                  />
                </Col>
              </Row>

              <Row className={isMobile ? "gx-3" : "gx-5"}>
                <Col md="4" className={`${isMobile ? "mt-2" : "my-2"}`}>
                  <StatusDisplay
                    label={Participant}
                    number={`${trainingImpression?.user_name} (${trainingImpression?.user_employee_id})`}
                  />
                </Col>
                <Col md="4" className={`${isMobile ? "mt-2" : "my-2"}`}>
                  <StatusDisplay
                    label={`${PreTest} ${Score}`}
                    number={
                      trainingImpression?.pre_test_score != null &&
                      !isNaN(trainingImpression?.pre_test_score)
                        ? trainingImpression.pre_test_score.toString()
                        : "-"
                    }
                  />
                </Col>
                <Col md="4" className={`${isMobile ? "mt-2" : "my-2"}`}>
                  <StatusDisplay
                    label={`${PostTest} ${Score}`}
                    number={
                      trainingImpression?.post_test_score != null &&
                      !isNaN(trainingImpression?.post_test_score)
                        ? trainingImpression.post_test_score.toString()
                        : "-"
                    }
                  />
                </Col>
              </Row>

              <Row className={isMobile ? "gx-3" : "gx-5"}>
                <Col md="4" className={`${isMobile ? "mt-2" : "my-2"}`}>
                  <StatusDisplay
                    label={Frequency}
                    number={
                      trainingImpressionFrequencyMap[
                        trainingImpression?.feedback_phase
                      ]
                    }
                  />
                </Col>
              </Row>
            </div>

            <div className="mb-2">
              <Divider />
            </div>
            <P className="mt-1 mb-1">{PleaseRateYourLevelAgreement}</P>
            <div className="mt-2">
              <StarLikertScale />
            </div>
            <div className="mt-3">
              {Object.entries(groupQuestionsByCategory())?.map(
                ([category, questionsList]) => (
                  <div className="mb-4" key={category}>
                    <CourseContentTable
                      key={category}
                      questions={questionsList}
                      handleRating={handleRating}
                      handleInputChange={handleInputChange}
                      category={formatString(category)}
                      review={isReportMode}
                    />
                  </div>
                )
              )}
            </div>
            {isReportMode && trainingImpression?.comment && (
              <Container className="course-content-feedback">
                <H4>
                  {CommentOnTrainingImpressionFromRm(
                    trainingImpression?.commented_user
                  )}
                </H4>
                <Divider />
                <P className="mt-1 mb-1">{trainingImpression?.comment}</P>
              </Container>
            )}
          </CardBody>
        </Card>
        {!isReportMode && (
          <FixedFooter>
            <SecondaryButton onClick={() => handleFinish("draft")}>
              {SaveAsDraft}
            </SecondaryButton>
            <DefaultButton
              onClick={() => handleFinish("submitted")}
              color="primary"
            >
              {Submit}
            </DefaultButton>
          </FixedFooter>
        )}
      </div>
    </div>
  );
};

export default TrainingImpressions;
